import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Container from "react-bootstrap/esm/Container";
import Card from "react-bootstrap/esm/Card";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import ListGroup from "react-bootstrap/esm/ListGroup";
import { applyGemeindeLanguage } from "../i18n/gemeindeLanguage";
import AppFooter from "../footer/AppFooter";

interface SwapSlot {
    date: string;
    serviceLabel: string;
    slot: 1 | 2;
    mode: "sermon" | "intro" | "reminder" | "choir";
}

interface SwapRequest {
    status: "pending" | "awaiting-approval" | "accepted" | "declined" | "expired" | "cancelled";
    gemeindeName: string;
    /** The Gemeinde's default language (GOD-50); missing from older functions. */
    gemeindeLanguage?: string;
    fromName: string;
    toName: string;
    service: SwapSlot | null;
    counter: SwapSlot | null;
    message: string;
    expiresAt: string;
}

/** What the server reports after an answer (SwapResult in functions/src/swapStore.ts). */
type SwapResult = "accepted" | "awaiting-approval" | "declined" | "expired" | "obsolete" | "not-open";

/**
 * Public page /tausch/:token: a colleague looks at a swap request and accepts
 * or declines it. The token in the link is the only credential.
 */
export default function SwapPage() {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const [swap, setSwap] = useState<SwapRequest | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "invalid" | "error">("loading");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<SwapResult | null>(null);

    const load = useCallback(async () => {
        const get = httpsCallable<{ token: string }, SwapRequest>(functions, "getSwapRequestByToken");
        const data = (await get({ token: token ?? "" })).data;
        applyGemeindeLanguage(data.gemeindeLanguage);
        setSwap(data);
        setState("ready");
    }, [token]);

    useEffect(() => {
        document.title = t("swap.title");
    }, [t]);

    useEffect(() => {
        load().catch((err: { code?: string }) => setState(err.code === "functions/not-found" ? "invalid" : "error"));
    }, [load]);

    const formatDate = (isoDate: string) =>
        new Date(isoDate).toLocaleDateString(t("calendar.locale") as string, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
        });

    const respond = async (accept: boolean) => {
        setBusy(true);
        try {
            const send = httpsCallable<{ token: string; accept: boolean }, { result: SwapResult }>(functions, "respondToSwap");
            setResult((await send({ token: token ?? "", accept })).data.result);
            await load();
        } catch {
            setState("error");
        } finally {
            setBusy(false);
        }
    };

    if (state === "loading") {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">{t("common.loading")}</p>
            </Container>
        );
    }

    if (state !== "ready" || !swap) {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="danger">
                    <Alert.Heading>{t("swap.invalidTitle")}</Alert.Heading>
                    <p className="mb-0">{t(state === "invalid" ? "swap.invalidText" : "errors.anErrorOccurred")}</p>
                </Alert>
            </Container>
        );
    }

    const describe = (slot: SwapSlot) => `${formatDate(slot.date)} · ${slot.serviceLabel}`;
    // What to tell the visitor: the answer just given wins over the stored status.
    const notice: { variant: string; text: string } | null =
        result === "accepted"
            ? { variant: "success", text: t("swap.resultAccepted") }
            : result === "awaiting-approval"
                ? { variant: "success", text: t("swap.resultAwaitingApproval") }
                : result === "declined"
                    ? { variant: "secondary", text: t("swap.resultDeclined") }
                    : result === "obsolete"
                        ? { variant: "warning", text: t("swap.resultObsolete") }
                        : swap.status !== "pending"
                            ? { variant: "secondary", text: t(`swap.status.${swap.status}`) }
                            : null;

    return (
        <>
            <Container className="mt-4" style={{ maxWidth: "600px" }}>
                <div className="gl-meta">{swap.gemeindeName}</div>
                <h3 className="mb-3">{t("swap.heading")}</h3>
                <Card>
                    <Card.Body>
                        <p>
                            {t(swap.counter ? "swap.introSwap" : "swap.introTakeover", { from: swap.fromName, to: swap.toName })}
                        </p>
                        <ListGroup variant="flush" className="mb-3">
                            {swap.service && (
                                <ListGroup.Item>
                                    <div className="gl-meta mb-1">{t("swap.youTake")}</div>
                                    {describe(swap.service)}
                                </ListGroup.Item>
                            )}
                            {swap.counter && (
                                <ListGroup.Item>
                                    <div className="gl-meta mb-1">{t("swap.theyTake", { from: swap.fromName })}</div>
                                    {describe(swap.counter)}
                                </ListGroup.Item>
                            )}
                            {swap.message && (
                                <ListGroup.Item>
                                    <div className="gl-meta mb-1">{t("swap.message")}</div>
                                    {swap.message}
                                </ListGroup.Item>
                            )}
                        </ListGroup>

                        {notice ? (
                            <Alert variant={notice.variant} className="mb-0">
                                {notice.text}
                            </Alert>
                        ) : (
                            <>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button className="flex-grow-1" disabled={busy} onClick={() => respond(true)}>
                                        {busy && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                                        {t("swap.accept")}
                                    </Button>
                                    <Button variant="outline-secondary" className="flex-grow-1" disabled={busy} onClick={() => respond(false)}>
                                        {t("swap.decline")}
                                    </Button>
                                </div>
                                <p className="small text-muted mt-3 mb-0">{t("swap.validUntil", { date: formatDate(swap.expiresAt) })}</p>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
            <AppFooter />
        </>
    );
}
